/**
 * Whether someone is signed in, without believing every flicker.
 *
 * Clerk sometimes reports a signed-in person as signed out for a moment and then
 * signed in again — mid-session, with nobody touching anything. The app used to
 * act on that moment at once: the account was cleared, the page unmounted, and
 * the route guard sent them to /login, which saw them signed in and sent them
 * straight back. Five people in thirty hours went page → /login → page in 0 to 4
 * seconds (2026-09-21/22), losing whatever they had typed on the way.
 *
 * So a signed-in → signed-out change is held for a few seconds. If Clerk says
 * signed in again inside that window, nothing ever happened as far as the app is
 * concerned. Someone who was never signed in is not held (there is nothing to
 * keep), and a sign-out the app asked for itself is not held either.
 */
import { useEffect, useRef, useState } from 'react';

export const SIGN_OUT_GRACE_MS = 4000;

let signOutExpected = false;
// What the app currently believes, for code outside React (the API error handler)
let believedSignedIn = false;

/** Whether the app currently takes the person to be signed in, flickers ridden out. */
export const isSteadilySignedIn = () => believedSignedIn;

/** Call just before signing out on purpose: that change is believed at once. */
export function expectSignOut() {
  signOutExpected = true;
}

/** Tests only */
export function __resetSteadySignedIn() {
  signOutExpected = false;
  believedSignedIn = false;
}

/**
 * @param loaded   Clerk has finished loading
 * @param signedIn Clerk's isSignedIn
 * @param userId   the Clerk user id while signed in
 * @returns {{ signedIn: boolean, userId: string|null, holding: boolean }}
 */
export function useSteadySignedIn(loaded, signedIn, userId, { graceMs = SIGN_OUT_GRACE_MS, now = Date.now } = {}) {
  // The person last seen signed in, and when Clerk then said they were not
  const last = useRef(null);
  const lostAt = useRef(null);
  const [, expire] = useState(0);

  const isIn = Boolean(loaded && signedIn && userId);
  if (isIn) {
    last.current = userId;
    lostAt.current = null;
  } else if (loaded && last.current !== null && lostAt.current === null) {
    // The first render that sees them gone decides whether to wait
    if (signOutExpected) {
      signOutExpected = false;
      lostAt.current = -Infinity;
    } else {
      lostAt.current = now();
    }
  }

  const holding = !isIn && lostAt.current !== null && now() - lostAt.current < graceMs;

  // Re-render when the window closes, so a real sign-out is acted on then
  useEffect(() => {
    if (!holding) return undefined;
    const left = graceMs - (now() - lostAt.current);
    const timer = setTimeout(() => expire((n) => n + 1), Math.max(0, left) + 1);
    return () => clearTimeout(timer);
  }, [holding]); // eslint-disable-line react-hooks/exhaustive-deps

  const steady = isIn
    ? { signedIn: true, userId, holding: false }
    : holding
      ? { signedIn: true, userId: last.current, holding: true }
      : { signedIn: false, userId: null, holding: false };
  if (loaded) believedSignedIn = steady.signedIn;
  return steady;
}
